
import { useEffect, useState } from "react";
import { OnboardingLayout } from "@/components/OnboardingLayout";
import { AnimatePresence } from "framer-motion";
import { CompletedScreen } from "@/components/onboarding/CompletedScreen";
import { AdminBanner } from "@/components/onboarding/AdminBanner";
import { DayCompletedScreen } from "@/components/onboarding/DayCompletedScreen";
import { StepContent } from "@/components/onboarding/StepContent";
import { useOnboardingForm } from "@/hooks/useOnboardingForm";
import { Loader2 } from "lucide-react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const Onboarding = () => {
  const [initializing, setInitializing] = useState(true);
  const [isLoadingProgress, setIsLoadingProgress] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const {
    currentStep,
    currentSubStep,
    state,
    inputValues,
    inputErrors,
    showDayCompleted,
    isProcessing,
    isAdmin,
    canProceed,
    handleInputChange,
    handleNextStep,
    continueToNextDay,
    handleReset,
    isTodoCompleted,
    loadSavedState
  } = useOnboardingForm();

  // Initialization - check for resume token and saved progress
  useEffect(() => {
    const init = async () => {
      try {
        setIsLoadingProgress(true);
        
        // Use user state from auth context for progress
        if (user?.savedstep) {
          console.log("Found saved progress:", user.savedstep);
          try {
            loadSavedState(user.savedstep);
            toast.success("Resuming from your last session");
          } catch (error) {
            console.error("Failed to load saved progress:", error);
            toast.error("Failed to load your progress");
          }
        }
        
        // Check for resume token in the URL
        const resumeToken = searchParams.get('resume');
        if (resumeToken) {
          // We don't actually use this anymore, but removing param from URL
          navigate('/onboarding', { replace: true });
        }
      } catch (error) {
        console.error("Error during initialization:", error);
      } finally {
        setIsLoadingProgress(false);
        setInitializing(false);
      }
    };

    init();
  }, [user?.savedstep]); 
  
  // Add logs for debugging timer visibility
  useEffect(() => {
    if (showDayCompleted) {
      console.log(`Day completed screen shown for day ${state.currentDayIndex + 1}`);
    }
  }, [showDayCompleted, state.currentDayIndex]);
  
  if (initializing || isLoadingProgress) {
    return (
      <OnboardingLayout>
        <div className="flex flex-col items-center justify-center min-h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-sm text-slate-500">Loading your progress...</p>
        </div>
      </OnboardingLayout>
    );
  }

  if (!currentStep) {
    return (
      <OnboardingLayout>
        <CompletedScreen />
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout>
      {isAdmin && (
        <AdminBanner onReset={handleReset} />
      )}
      
      <AnimatePresence mode="wait">
        {showDayCompleted ? (
          <DayCompletedScreen 
            currentDayIndex={state.currentDayIndex} 
            onContinue={continueToNextDay}
            isAdmin={isAdmin}
          />
        ) : (
          <StepContent
            currentStep={currentStep}
            currentSubStep={currentSubStep}
            state={state}
            inputValues={inputValues}
            inputErrors={inputErrors}
            canProceed={canProceed}
            isProcessing={isProcessing}
            handleInputChange={handleInputChange}
            handleNextStep={handleNextStep}
            isTodoCompleted={isTodoCompleted}
          />
        )}
      </AnimatePresence>
    </OnboardingLayout>
  );
};

export default Onboarding;
